import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, LogIn, LogOut, Loader2, Sparkles, Users, Wallet, Activity, ArrowLeft, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { adminLogin, adminLogout, adminOverview } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Admin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isGoogleAdmin = !!user?.is_admin;

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminOverview();
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // re-load when Google auth state changes (Chris signs in -> auto-unlock)
  }, [isGoogleAdmin]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      await adminLogin(password);
      await load();
      toast.success("Welcome back, admin.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wrong password");
    } finally {
      setSubmitting(false);
    }
  };

  const onLogout = async () => {
    await adminLogout();
    setData(null);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/"
          data-testid="admin-back-home"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-slate-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <h1 className="font-display text-4xl sm:text-5xl font-semibold text-slate-900 inline-flex items-center gap-3">
          <Shield className="w-9 h-9 text-slate-700" /> Admin panel
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          {isGoogleAdmin
            ? "Signed in as a Google admin. Password not required."
            : "Password-protected overview of players, XP earned, and the platform wallet balance. If you're an admin, sign in with Google from the home page to skip the password."}
        </p>
        {isGoogleAdmin && data && (
          <div
            data-testid="admin-google-chip"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 px-3 py-1.5 text-sm font-bold border border-emerald-200"
          >
            <BadgeCheck className="w-4 h-4" /> Admin · {user.name}
          </div>
        )}

        {loading ? (
          <div className="mt-10 inline-flex items-center gap-2 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : !data ? (
          <form
            onSubmit={onSubmit}
            data-testid="admin-login-form"
            className="mt-8 max-w-md bg-white border-2 border-slate-100 rounded-2xl p-6 shadow-sm"
          >
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">
              Admin password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              data-testid="admin-password-input"
              autoComplete="current-password"
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-slate-900 outline-none font-bold"
            />
            <button
              type="submit"
              disabled={submitting || !password}
              data-testid="admin-login-submit"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-xl disabled:opacity-50 transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              Sign in
            </button>
          </form>
        ) : (
          <>
            <div className="mt-8 grid gap-4 sm:grid-cols-4">
              <Stat icon={Users} label="Players" value={data.total_users} testid="admin-stat-users" />
              <Stat icon={Sparkles} label="Total XP awarded" value={formatXp(data.total_xp_awarded)} testid="admin-stat-xp" />
              <Stat icon={Activity} label="Award events" value={data.total_award_events} testid="admin-stat-events" />
              <Stat icon={Wallet} label={`Admin wallet (${pct(data.admin_fee_pct)})`} value={formatXp(data.admin_pool_xp)} testid="admin-stat-pool" />
            </div>

            <div className="mt-10 bg-white border-2 border-slate-100 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl font-semibold text-slate-900">Top players</h2>
                <button
                  onClick={onLogout}
                  data-testid="admin-sign-out"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900"
                >
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
              </div>
              {data.top_users.length === 0 ? (
                <p className="text-slate-500" data-testid="admin-empty">No players yet.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.top_users.map((u, i) => (
                    <div
                      key={u.user_id}
                      data-testid={`admin-user-row-${i}`}
                      className="flex items-center gap-3 py-3"
                    >
                      <span className="w-6 text-sm font-bold text-slate-400 text-right">
                        {i + 1}
                      </span>
                      {u.picture ? (
                        <img
                          src={u.picture}
                          alt=""
                          className="w-8 h-8 rounded-full"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-slate-200" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-bold text-slate-800">{u.name}</div>
                        <div className="truncate text-xs text-slate-500">{u.email}</div>
                      </div>
                      <div className="text-amber-700 font-bold">{formatXp(u.xp)} XP</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border-2 border-slate-100 rounded-2xl p-5 shadow-sm"
    >
      <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-2 font-display text-3xl font-semibold text-slate-900">
        {value}
      </div>
    </div>
  );
}

function formatXp(n) {
  const num = Number(n) || 0;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return num % 1 === 0 ? String(num) : num.toFixed(2);
}

function pct(p) {
  return `${((Number(p) || 0) * 100).toFixed(1)}%`;
}
